/**
 * Permissões do chat: os mesmos bits e o mesmo cálculo do Discord
 * (https://docs.discord.com/developers/topics/permissions). Os overwrites
 * importados do backup usam estes números, então nada é convertido.
 *
 * Fica no contrato porque o cliente usa o mesmo cálculo para esconder botões e
 * explicar por que alguém não pode algo. Quem decide é sempre a API.
 *
 * Só funções puras e `bigint`: o bitfield passa de 2^53.
 */

export const PermissionFlags = {
  CREATE_INSTANT_INVITE: 1n << 0n,
  KICK_MEMBERS: 1n << 1n,
  BAN_MEMBERS: 1n << 2n,
  ADMINISTRATOR: 1n << 3n,
  MANAGE_CHANNELS: 1n << 4n,
  MANAGE_GUILD: 1n << 5n,
  ADD_REACTIONS: 1n << 6n,
  VIEW_AUDIT_LOG: 1n << 7n,
  PRIORITY_SPEAKER: 1n << 8n,
  STREAM: 1n << 9n,
  VIEW_CHANNEL: 1n << 10n,
  SEND_MESSAGES: 1n << 11n,
  SEND_TTS_MESSAGES: 1n << 12n,
  MANAGE_MESSAGES: 1n << 13n,
  EMBED_LINKS: 1n << 14n,
  ATTACH_FILES: 1n << 15n,
  READ_MESSAGE_HISTORY: 1n << 16n,
  MENTION_EVERYONE: 1n << 17n,
  USE_EXTERNAL_EMOJIS: 1n << 18n,
  VIEW_GUILD_INSIGHTS: 1n << 19n,
  CONNECT: 1n << 20n,
  SPEAK: 1n << 21n,
  MUTE_MEMBERS: 1n << 22n,
  DEAFEN_MEMBERS: 1n << 23n,
  MOVE_MEMBERS: 1n << 24n,
  USE_VAD: 1n << 25n,
  CHANGE_NICKNAME: 1n << 26n,
  MANAGE_NICKNAMES: 1n << 27n,
  MANAGE_ROLES: 1n << 28n,
  MANAGE_WEBHOOKS: 1n << 29n,
  MANAGE_GUILD_EXPRESSIONS: 1n << 30n,
  USE_APPLICATION_COMMANDS: 1n << 31n,
  REQUEST_TO_SPEAK: 1n << 32n,
  MANAGE_EVENTS: 1n << 33n,
  MANAGE_THREADS: 1n << 34n,
  CREATE_PUBLIC_THREADS: 1n << 35n,
  CREATE_PRIVATE_THREADS: 1n << 36n,
  USE_EXTERNAL_STICKERS: 1n << 37n,
  SEND_MESSAGES_IN_THREADS: 1n << 38n,
  USE_EMBEDDED_ACTIVITIES: 1n << 39n,
  MODERATE_MEMBERS: 1n << 40n,
  VIEW_CREATOR_MONETIZATION_ANALYTICS: 1n << 41n,
  USE_SOUNDBOARD: 1n << 42n,
  CREATE_GUILD_EXPRESSIONS: 1n << 43n,
  CREATE_EVENTS: 1n << 44n,
  USE_EXTERNAL_SOUNDS: 1n << 45n,
  SEND_VOICE_MESSAGES: 1n << 46n,
  SET_VOICE_CHANNEL_STATUS: 1n << 48n,
  SEND_POLLS: 1n << 49n,
  USE_EXTERNAL_APPS: 1n << 50n,
  PIN_MESSAGES: 1n << 51n,
  BYPASS_SLOWMODE: 1n << 52n,
} as const;

export type PermissionFlag = keyof typeof PermissionFlags;

export const ALL_PERMISSIONS: bigint = Object.values(PermissionFlags).reduce(
  (all, bit) => all | bit,
  0n,
);

const F = PermissionFlags;

/** Negar Enviar mensagens nega também estes (regra implícita do Discord). */
const IMPLIED_BY_SEND =
  F.MENTION_EVERYONE | F.SEND_TTS_MESSAGES | F.ATTACH_FILES | F.EMBED_LINKS;

/** O que sobra para quem está de castigo ou ainda não aceitou as regras. */
const TIMEOUT_ALLOWED = F.VIEW_CHANNEL | F.READ_MESSAGE_HISTORY;

/**
 * O que se pode numa DM ou grupo: o Discord não tem cargos ali, quem
 * participa tem as permissões de texto. Apagar mensagem de outra pessoa,
 * não (só a própria).
 */
export const DM_PERMISSIONS =
  F.VIEW_CHANNEL |
  F.SEND_MESSAGES |
  F.SEND_TTS_MESSAGES |
  F.EMBED_LINKS |
  F.ATTACH_FILES |
  F.READ_MESSAGE_HISTORY |
  F.ADD_REACTIONS |
  F.USE_EXTERNAL_EMOJIS |
  F.USE_EXTERNAL_STICKERS |
  F.SEND_POLLS |
  F.PIN_MESSAGES;

/** Tipos de canal que são thread (herdam as permissões do canal pai). */
export const THREAD_CHANNEL_TYPES: ReadonlySet<number> = new Set([10, 11, 12]);

export const OverwriteType = { ROLE: 0, MEMBER: 1 } as const;
export type OverwriteType = (typeof OverwriteType)[keyof typeof OverwriteType];

export interface PermissionOverwrite {
  /** ID do cargo (o do @everyone é o ID do servidor) ou do usuário. */
  id: string;
  type: OverwriteType;
  allow: bigint;
  deny: bigint;
}

export interface PermissionRole {
  id: string;
  permissions: bigint;
  position: number;
}

export interface PermissionGuild {
  id: string;
  ownerId: string;
  /** Todos os cargos do servidor, inclusive o @everyone (ID = ID do servidor). */
  roles: ReadonlyMap<string, PermissionRole>;
}

export interface PermissionMember {
  userId: string;
  /** Cargos do membro, sem o @everyone (ele é implícito). */
  roleIds: readonly string[];
  /** Castigo: até quando. Nulo ou no passado = sem castigo. */
  communicationDisabledUntil?: Date | string | null;
  /**
   * Ainda não aceitou as regras do servidor (verificação de membros): como
   * no Discord, só vê e lê até aceitar.
   */
  pending?: boolean;
}

export interface PermissionChannel {
  type: number;
  overwrites: readonly PermissionOverwrite[];
}

export function toPermissionBits(
  value: string | number | bigint | null | undefined,
): bigint {
  if (value === null || value === undefined || value === '') return 0n;
  return BigInt(value);
}

export function hasPermission(bits: bigint, flag: bigint): boolean {
  return (bits & flag) === flag;
}

export function permissionNames(bits: bigint): PermissionFlag[] {
  return (Object.keys(PermissionFlags) as PermissionFlag[]).filter((name) =>
    hasPermission(bits, PermissionFlags[name]),
  );
}

export function isTimedOut(
  member: PermissionMember,
  now: Date = new Date(),
): boolean {
  const until = member.communicationDisabledUntil;
  if (!until) return false;
  return new Date(until).getTime() > now.getTime();
}

/** @everyone + cargos. Dono e Administrador têm tudo. Sem regra de castigo. */
export function computeBasePermissions(
  guild: PermissionGuild,
  member: PermissionMember,
): bigint {
  if (member.userId === guild.ownerId) return ALL_PERMISSIONS;

  let permissions = guild.roles.get(guild.id)?.permissions ?? 0n;
  for (const roleId of member.roleIds) {
    permissions |= guild.roles.get(roleId)?.permissions ?? 0n;
  }
  if (hasPermission(permissions, F.ADMINISTRATOR)) return ALL_PERMISSIONS;
  return permissions;
}

/**
 * Overwrites do canal sobre as permissões base, na ordem do Discord:
 * @everyone do canal → cargos do membro (todas as negações, depois todas as
 * permissões) → o próprio membro. Sem regras implícitas nem castigo: é o
 * número que o discord.js devolve em `permissionsFor`.
 */
export function computeOverwrites(
  base: bigint,
  guild: PermissionGuild,
  member: PermissionMember,
  overwrites: readonly PermissionOverwrite[],
): bigint {
  if (hasPermission(base, F.ADMINISTRATOR)) return ALL_PERMISSIONS;

  const byId = new Map(
    overwrites.map((overwrite) => [overwrite.id, overwrite]),
  );
  let permissions = base;

  const everyone = byId.get(guild.id);
  if (everyone) {
    permissions &= ~everyone.deny;
    permissions |= everyone.allow;
  }

  let allow = 0n;
  let deny = 0n;
  for (const roleId of member.roleIds) {
    const overwrite = byId.get(roleId);
    if (overwrite?.type !== OverwriteType.ROLE) continue;
    allow |= overwrite.allow;
    deny |= overwrite.deny;
  }
  permissions &= ~deny;
  permissions |= allow;

  const own = byId.get(member.userId);
  if (own?.type === OverwriteType.MEMBER) {
    permissions &= ~own.deny;
    permissions |= own.allow;
  }

  return permissions;
}

/** De castigo ou pendente (não aceitou as regras): só ver e ler. */
function isRestricted(member: PermissionMember, now?: Date): boolean {
  return !!member.pending || isTimedOut(member, now);
}

/**
 * Permissões no servidor (ações que não dependem de canal: gerenciar cargos,
 * banir, castigar). Castigo e regras não aceitas tiram tudo, menos ver e ler.
 */
export function computeGuildPermissions(
  guild: PermissionGuild,
  member: PermissionMember,
  now: Date = new Date(),
): bigint {
  const base = computeBasePermissions(guild, member);
  if (base === ALL_PERMISSIONS) return base;
  return isRestricted(member, now) ? base & TIMEOUT_ALLOWED : base;
}

/**
 * Permissões efetivas num canal: base + overwrites + castigo + regras
 * implícitas. Para thread, passe o canal pai: a thread não tem overwrites e
 * herda as dele, com "Enviar em threads" no lugar de "Enviar mensagens".
 */
export function computeChannelPermissions(
  guild: PermissionGuild,
  member: PermissionMember,
  channel: PermissionChannel,
  options: { now?: Date; isThread?: boolean } = {},
): bigint {
  const base = computeBasePermissions(guild, member);
  if (base === ALL_PERMISSIONS) return ALL_PERMISSIONS;

  let permissions = computeOverwrites(base, guild, member, channel.overwrites);

  if (isRestricted(member, options.now)) permissions &= TIMEOUT_ALLOWED;
  if (!hasPermission(permissions, F.VIEW_CHANNEL)) return 0n;

  const sendFlag =
    (options.isThread ?? THREAD_CHANNEL_TYPES.has(channel.type))
      ? F.SEND_MESSAGES_IN_THREADS
      : F.SEND_MESSAGES;
  if (!hasPermission(permissions, sendFlag)) permissions &= ~IMPLIED_BY_SEND;

  return permissions;
}

const PRIVATE_THREAD = 12;

/**
 * Permissões efetivas de alguém num canal, como o Discord: thread usa os
 * overwrites do canal pai (e `SEND_MESSAGES_IN_THREADS` para escrever);
 * thread privada some para quem não participa dela nem tem `MANAGE_THREADS`.
 * A mesma regra serve a REST, o gateway e o cliente.
 */
export function computeEffectiveChannelPermissions(input: {
  guild: PermissionGuild;
  member: PermissionMember;
  channelType: number;
  /** Overwrites do próprio canal ou, numa thread, do canal pai. */
  overwrites: readonly PermissionOverwrite[];
  /** A pessoa participa (só importa em thread privada). */
  joined: boolean;
  now?: Date;
}): bigint {
  const permissions = computeChannelPermissions(
    input.guild,
    input.member,
    { type: input.channelType, overwrites: input.overwrites },
    { now: input.now, isThread: THREAD_CHANNEL_TYPES.has(input.channelType) },
  );
  if (
    input.channelType === PRIVATE_THREAD &&
    !input.joined &&
    !hasPermission(permissions, F.MANAGE_THREADS)
  ) {
    return 0n;
  }
  return permissions;
}

// ---------------------------------------------------------------------------
// Explicação ("Verificar acesso"): qual passo decidiu uma permissão.
// ---------------------------------------------------------------------------

export type PermissionDecision =
  | { allowed: true; by: 'owner' | 'administrator' }
  | {
      allowed: false;
      by: 'timeout' | 'pending' | 'no-view-channel' | 'no-send-messages';
    }
  | {
      allowed: boolean;
      by: 'member-overwrite';
      userId: string;
    }
  | {
      allowed: boolean;
      by: 'role-overwrite';
      /** Cargos do membro cujo overwrite neste canal decidiu (permite ou nega). */
      roleIds: string[];
      /** Na negação: cargos do membro que poderiam liberar mas não liberam aqui. */
      withoutExplicitAllow?: string[];
    }
  | { allowed: boolean; by: 'everyone-overwrite' }
  | {
      allowed: boolean;
      by: 'roles';
      /** Cargos (ou o @everyone) que concedem a permissão no servidor. */
      roleIds: string[];
    };

/**
 * Por que o membro pode (ou não) `flag` no canal, em termos que a interface
 * mostra: dono, administrador, castigo, regras não aceitas, falta de "Ver
 * canal", overwrite do membro, overwrite de cargos, overwrite do @everyone ou
 * cargos do servidor.
 */
export function explainChannelPermission(
  guild: PermissionGuild,
  member: PermissionMember,
  channel: PermissionChannel,
  flag: bigint,
  options: { now?: Date; isThread?: boolean } = {},
): PermissionDecision {
  if (member.userId === guild.ownerId) return { allowed: true, by: 'owner' };
  const base = computeBasePermissions(guild, member);
  if (base === ALL_PERMISSIONS) return { allowed: true, by: 'administrator' };

  const effective = computeChannelPermissions(guild, member, channel, options);
  const isThread = options.isThread ?? THREAD_CHANNEL_TYPES.has(channel.type);
  const sendFlag = isThread ? F.SEND_MESSAGES_IN_THREADS : F.SEND_MESSAGES;

  if (!hasPermission(effective, flag)) {
    const raw = computeOverwrites(base, guild, member, channel.overwrites);
    if (isTimedOut(member, options.now) && !(TIMEOUT_ALLOWED & flag)) {
      return { allowed: false, by: 'timeout' };
    }
    if (member.pending && !(TIMEOUT_ALLOWED & flag)) {
      return { allowed: false, by: 'pending' };
    }
    if (flag !== F.VIEW_CHANNEL && !hasPermission(raw, F.VIEW_CHANNEL)) {
      return { allowed: false, by: 'no-view-channel' };
    }
    if (
      (IMPLIED_BY_SEND & flag) === flag &&
      hasPermission(raw, flag) &&
      !hasPermission(raw, sendFlag)
    ) {
      return { allowed: false, by: 'no-send-messages' };
    }
  }

  const byId = new Map(
    channel.overwrites.map((overwrite) => [overwrite.id, overwrite]),
  );
  const own = byId.get(member.userId);
  if (own?.type === OverwriteType.MEMBER) {
    if (own.allow & flag) {
      return { allowed: true, by: 'member-overwrite', userId: member.userId };
    }
    if (own.deny & flag) {
      return { allowed: false, by: 'member-overwrite', userId: member.userId };
    }
  }

  const allowingRoles: string[] = [];
  const denyingRoles: string[] = [];
  for (const roleId of member.roleIds) {
    const overwrite = byId.get(roleId);
    if (overwrite?.type !== OverwriteType.ROLE) continue;
    if (overwrite.allow & flag) allowingRoles.push(roleId);
    if (overwrite.deny & flag) denyingRoles.push(roleId);
  }
  if (allowingRoles.length) {
    return { allowed: true, by: 'role-overwrite', roleIds: allowingRoles };
  }
  if (denyingRoles.length) {
    const withoutExplicitAllow = member.roleIds.filter(
      (roleId) =>
        !denyingRoles.includes(roleId) &&
        (guild.roles.get(roleId)?.permissions ?? 0n) & flag,
    );
    return {
      allowed: false,
      by: 'role-overwrite',
      roleIds: denyingRoles,
      withoutExplicitAllow,
    };
  }

  const everyone = byId.get(guild.id);
  if (everyone?.allow && everyone.allow & flag) {
    return { allowed: true, by: 'everyone-overwrite' };
  }
  if (everyone?.deny && everyone.deny & flag) {
    return { allowed: false, by: 'everyone-overwrite' };
  }

  const grantingRoles = [guild.id, ...member.roleIds].filter(
    (roleId) => (guild.roles.get(roleId)?.permissions ?? 0n) & flag,
  );
  return {
    allowed: grantingRoles.length > 0,
    by: 'roles',
    roleIds: grantingRoles,
  };
}

// ---------------------------------------------------------------------------
// Hierarquia de cargos
// ---------------------------------------------------------------------------

/**
 * Ordem dos cargos como no Discord: maior `position` fica acima; empate,
 * o de ID menor (mais antigo) fica acima. Positivo = `a` acima de `b`.
 */
export function compareRolePositions(
  a: PermissionRole,
  b: PermissionRole,
): number {
  if (a.position !== b.position) return a.position - b.position;
  const idA = BigInt(a.id);
  const idB = BigInt(b.id);
  return idA < idB ? 1 : idA > idB ? -1 : 0;
}

/** Cargo mais alto do membro (o @everyone se não tiver nenhum). */
export function highestRole(
  guild: PermissionGuild,
  member: PermissionMember,
): PermissionRole | undefined {
  let top = guild.roles.get(guild.id);
  for (const roleId of member.roleIds) {
    const role = guild.roles.get(roleId);
    if (!role) continue;
    if (!top || compareRolePositions(role, top) > 0) top = role;
  }
  return top;
}

/** Quem pode gerenciar (cargos, apelido, castigo, expulsar, banir) o alvo. */
export function canManageMember(
  guild: PermissionGuild,
  actor: PermissionMember,
  target: PermissionMember,
): boolean {
  if (target.userId === guild.ownerId) return false;
  if (actor.userId === guild.ownerId) return true;
  if (actor.userId === target.userId) return false;
  const actorTop = highestRole(guild, actor);
  const targetTop = highestRole(guild, target);
  if (!actorTop || !targetTop) return false;
  return compareRolePositions(actorTop, targetTop) > 0;
}

/** Quem pode editar, dar ou tirar o cargo: só se ele estiver abaixo do seu mais alto. */
export function canManageRole(
  guild: PermissionGuild,
  actor: PermissionMember,
  role: PermissionRole,
): boolean {
  if (actor.userId === guild.ownerId) return true;
  if (role.id === guild.id) return false;
  const actorTop = highestRole(guild, actor);
  return !!actorTop && compareRolePositions(actorTop, role) > 0;
}
